import { Filter, RotateCcw, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/modern/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { cn } from "@/modern/utils";
import type { SurfaceFeedFilters, SurfaceFilterTag } from "../api/types";
import { hasActiveSurfaceFilters } from "../api/types";

type Props = {
  filters: SurfaceFeedFilters;
  filterTags: SurfaceFilterTag[];
  tagsLoading?: boolean;
  onChange: (next: SurfaceFeedFilters) => void;
  onReanalyzePending?: () => void;
  reanalyzeBusy?: boolean;
};

type TagFilterMode = "off" | "include" | "exclude";

function modeForTag(
  id: number,
  includeIds: number[],
  excludeIds: number[]
): TagFilterMode {
  if (includeIds.includes(id)) return "include";
  if (excludeIds.includes(id)) return "exclude";
  return "off";
}

/**
 * Compact header Filters control — one tag list with include/exclude per row.
 */
export default function PromoteSurfaceFilterBar({
  filters,
  filterTags,
  tagsLoading,
  onChange,
  onReanalyzePending,
  reanalyzeBusy,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const active = hasActiveSurfaceFilters(filters);
  const includeIds = filters.tagIds ?? [];
  const excludeIds = filters.excludeTagIds ?? [];
  const includeCount = includeIds.length;
  const excludeCount = excludeIds.length;
  const chipCount = includeCount + excludeCount;

  const sortedTags = useMemo(
    () => [...filterTags].sort((a, b) => a.label.localeCompare(b.label)),
    [filterTags]
  );

  const filteredTags = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedTags;
    return sortedTags.filter(
      (t) =>
        t.label.toLowerCase().includes(q) ||
        (t.type ?? "").toLowerCase().includes(q) ||
        (t.tag_group ?? "").toLowerCase().includes(q)
    );
  }, [sortedTags, query]);

  const setTagMode = (id: number, mode: TagFilterMode) => {
    const nextInclude = includeIds.filter((x) => x !== id);
    const nextExclude = excludeIds.filter((x) => x !== id);
    if (mode === "include") nextInclude.push(id);
    if (mode === "exclude") nextExclude.push(id);
    onChange({ ...filters, tagIds: nextInclude, excludeTagIds: nextExclude });
  };

  const clearFilters = () => {
    setQuery("");
    onChange({ tagIds: [], excludeTagIds: [], taskId: null });
  };

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={cn(
              "h-8 gap-1.5 rounded-full px-2.5 text-xs font-semibold shadow-none sm:h-9 [&_svg]:size-3.5",
              active && "border-primary/40 bg-primary/5"
            )}
            aria-label="Filters"
          >
            <Filter className="h-3.5 w-3.5" aria-hidden />
            Filters
            {chipCount > 0 && (
              <span className="tabular-nums text-muted-foreground">{chipCount}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[22rem] space-y-3 p-3 sm:w-[24rem]">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tags…"
              className={cn(
                "h-9 w-full rounded-md border border-foreground/20 bg-background pl-8 pr-3 text-sm text-foreground shadow-sm",
                "placeholder:text-muted-foreground/80",
                "outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40"
              )}
              aria-label="Search tags"
            />
          </div>

          <div className="overflow-hidden rounded-md border border-border">
            {tagsLoading ? (
              <p className="px-3 py-3 text-xs text-muted-foreground">Loading tags…</p>
            ) : !sortedTags.length ? (
              <p className="px-3 py-3 text-xs text-muted-foreground">
                No filter tags on this board.
              </p>
            ) : (
              <ul className="max-h-[min(28rem,70vh)] space-y-0.5 overflow-y-auto p-1 [scrollbar-width:thin]">
                {filteredTags.length === 0 ? (
                  <li className="px-2 py-2 text-xs text-muted-foreground">No tags match.</li>
                ) : (
                  filteredTags.map((tag) => {
                    const mode = modeForTag(tag.id, includeIds, excludeIds);
                    return (
                      <li
                        key={tag.id}
                        className={cn(
                          "flex items-center gap-2 rounded-md px-2 py-1.5",
                          mode !== "off" && "bg-muted/50"
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm leading-tight">{tag.label}</p>
                          {tag.type && (
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              {tag.type}
                            </p>
                          )}
                        </div>
                        <div
                          className="flex shrink-0 items-center gap-1"
                          role="group"
                          aria-label={`Filter mode for ${tag.label}`}
                        >
                          <ModeButton
                            active={mode === "include"}
                            tone="include"
                            onClick={() =>
                              setTagMode(tag.id, mode === "include" ? "off" : "include")
                            }
                          >
                            Include
                          </ModeButton>
                          <ModeButton
                            active={mode === "exclude"}
                            tone="exclude"
                            onClick={() =>
                              setTagMode(tag.id, mode === "exclude" ? "off" : "exclude")
                            }
                          >
                            Exclude
                          </ModeButton>
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            )}
          </div>

          <p className="text-[10px] leading-snug text-muted-foreground">
            Include keeps matching assets · Exclude hides them · one mode per tag
          </p>

          {active && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 w-full text-xs text-muted-foreground"
              onClick={clearFilters}
            >
              Clear all filters
            </Button>
          )}
        </PopoverContent>
      </Popover>

      {includeCount > 0 && (
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] tabular-nums text-primary">
          {includeCount} included
        </span>
      )}
      {excludeCount > 0 && (
        <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] tabular-nums text-destructive">
          {excludeCount} excluded
        </span>
      )}

      {active && (
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground sm:h-9 sm:w-9"
          onClick={clearFilters}
          aria-label="Clear filters"
          title="Clear filters"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}

      {onReanalyzePending && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="ml-auto h-7 gap-1 rounded-full px-2 text-xs text-muted-foreground"
          disabled={reanalyzeBusy}
          onClick={onReanalyzePending}
          title="Regenerate all pending Review drafts"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden />
          <span className="hidden sm:inline">Reanalyze</span>
        </Button>
      )}
    </div>
  );
}

function ModeButton({
  active,
  tone,
  onClick,
  children,
}: {
  active: boolean;
  tone: "include" | "exclude";
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "h-7 min-w-[3.75rem] rounded-md border px-2 text-[11px] font-semibold transition",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        active
          ? tone === "include"
            ? "border-primary bg-primary text-primary-foreground shadow-sm"
            : "border-destructive bg-destructive text-destructive-foreground shadow-sm"
          : "border-foreground/20 bg-background text-muted-foreground hover:border-foreground/35 hover:bg-muted hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}
